import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import { TrayImageCapture } from 'components/TrayImageCapture/TrayImageCapture';
import { CheckOutTraysBySet, useCheckOutTraysBySet } from 'hooks/useCheckOutTraysBySet';
import { useHistoryStack } from 'hooks/useHistoryStack';
import { useImageUploadManager } from 'hooks/useImageUploadManager';
import useSetDisplayLabel from 'hooks/useSetDisplayLabel';
import { useVisibleCheckOutDialog } from 'hooks/useVisibleCheckOutDialog';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';

export const CheckOutTrayImageCapture = () => {
  const { deliveryId = '', setId: setKitInstanceId = '', trayId = '' } = useParams();

  const { data: delivery } = useDelivery(deliveryId);
  const { data: currentUser } = useCurrentUser();
  const [checkOutTraysBySet, setCheckOutTraysBySet] = useCheckOutTraysBySet();
  const { queueImageForUpload } = useImageUploadManager();

  const navigate = useNavigate();

  const deliverySet = useMemo(
    () => delivery?.sets.find(set => set.kitInstanceId === setKitInstanceId),
    [delivery, setKitInstanceId],
  );
  const setName = useSetDisplayLabel(deliverySet);
  const tray = useMemo(
    () => deliverySet?.trays.find(tray => tray.trayInstanceId === trayId),
    [deliverySet?.trays, trayId],
  );
  const trayName = tray?.name || '';

  useEffect(() => {
    // if the tray is not in the checkOutTraysBySet, then the user has navigated to the page directly
    // and we should navigate them back to the check out page
    if (!tray) {
      navigate(Routes.CheckOut.path.replace(DELIVERY_ID_PARAM, deliveryId));
    }
  }, [deliveryId, navigate, tray]);

  const historyStack = useHistoryStack()[0];
  const { haveCheckOutDialogHistory, prevVisibleCheckOutDialog, setVisibleCheckOutDialog } = useVisibleCheckOutDialog();

  const onBackClick = useCallback(() => {
    const referrer = historyStack.reverse().find(h => !h.pathname.includes('/capture-image'));
    prevVisibleCheckOutDialog();
    navigate((referrer?.pathname || -1) as any);
  }, [historyStack, navigate, prevVisibleCheckOutDialog]);

  const onImageCapture = useCallback(
    async (imageDataUri: string) => {
      if (!deliveryId) {
        throw new Error('No deliveryId provided');
      }

      if (!delivery) {
        throw new Error('No delivery found');
      }

      if (!currentUser) {
        throw new Error('No user found');
      }

      if (!trayName) {
        throw new Error('No tray found');
      }

      // store the data uri with check in trays, clearing any stale gsImagePath/uploadPromise
      // from a previous capture of this tray so the fresh upload below is the source of truth
      const updatedCheckOutTraysBySet: CheckOutTraysBySet = {
        ...checkOutTraysBySet,
        [setKitInstanceId]: {
          ...checkOutTraysBySet[setKitInstanceId],
          trays: {
            ...checkOutTraysBySet[setKitInstanceId].trays,
            [trayId]: {
              ...checkOutTraysBySet[setKitInstanceId].trays[trayId],
              imageDataUri,
              gsImagePath: undefined,
              uploadPromise: undefined,
            },
          },
        },
      };

      setCheckOutTraysBySet(updatedCheckOutTraysBySet);

      // immediately queue the upload to run in the background, non-blocking, while the user
      // continues on with the check out flow
      queueImageForUpload(
        { stage: 'CHECK_OUT', setKitInstanceId, trayId },
        { stage: 'CHECK_OUT', deliveryId, setId: setKitInstanceId, trayId },
        imageDataUri,
      );

      await trackEvent(EventType.CaptureTrayImageCheckIn, {
        delivery_id: deliveryId,
        order_id: delivery.orderNumber || '',
        set_id: setKitInstanceId,
        current_user: currentUser.userId || '',
        tenant_name: currentUser.tenantName || '',
        tray_name: trayName,
      });

      const uri = Routes.CheckOutTraySubmit.makePath(deliveryId, setKitInstanceId, trayId);
      if (haveCheckOutDialogHistory) {
        setVisibleCheckOutDialog({
          type: 'PAGE_URI',
          uri,
        });
        return;
      }

      navigate(uri);
    },
    [
      deliveryId,
      delivery,
      currentUser,
      trayName,
      checkOutTraysBySet,
      setKitInstanceId,
      trayId,
      setCheckOutTraysBySet,
      queueImageForUpload,
      haveCheckOutDialogHistory,
      navigate,
      setVisibleCheckOutDialog,
    ],
  );

  return (
    <TrayImageCapture setName={setName} trayName={trayName} onBackClick={onBackClick} onImageCapture={onImageCapture} />
  );
};
